/**
 *
 * @author Alexander Nagelberg
 *
 * @prop {Object} affiliate - { id, name } for affiliation to fetch contacts for
 *
 * @example
 * <AdminContactCards affiliate={ { id: 12345, name: 'LENDER' } } />
 * Displays contacts for Lender 12345 in a table or list
 *
 */

import React from "react";
import { connect } from "react-redux";

import { fetchAdminContacts } from "../actions/profileActions";

const AdminTable = ({ admins }) => {
  return (
    <div>
      <table style={{ margin: "0 0 2em" }}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
          </tr>
        </thead>
        <tbody>
          {admins.map(admin => (
            <AdminRow
              name={admin?.name}
              email={admin?.email}
              phone={admin?.phone?.replace(
                /(\d{3})(\d{3})(\d{4})/,
                "($1) $2-$3"
              )}
              key={admin?.name + admin?.email}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AdminRow = ({ name, email, phone }) => {
  return (
    <tr>
      <td data-label='Name'>{name ?? 'null'}</td>
      <td data-label='Email'>
        {email === null ? 'null' : <a href={`mailto:${email}`}>{email}</a>}
      </td>
      <td data-label='Phone'>{phone ?? 'null'}</td>
    </tr>
  );
};

const AdminList = ({ admins }) => {
  return (
    <div>
      <ul>
        {admins.map(admin => (
          <li key={admin?.name}>{admin?.name ?? 'null'}</li>
        ))}
      </ul>
    </div>
  );
};

class AdminContactCards extends React.Component {
  fetchData() {
    this.props.fetchAdminContacts({
      affiliateId: this.props.affiliate.id,
      affiliationType: this.props.affiliate.name
    });
  }

  componentDidMount() {
    this.fetchData();
  }

  // we use this lifecycle to make another fetch only if props changed
  // React tells us not to use getDerivedStateFromProps for side effects
  componentDidUpdate(prevProps) {
    if (prevProps.affiliate.id !== this.props.affiliate.id) {
      this.fetchData();
    }
  }

  render() {
    const admins = this.props.adminList.admins;
    if (this.props.adminList.fetching) {
      return <div>Loading admin contacts...</div>;
    } else if (this.props.adminList.errors) {
      return <div>Unable to retrieve admins to contact</div>;
    } else if (admins !== undefined) {
      if (admins.length < 1) {
        return (
          <div>
            <br />
            No admins' contact information from{" "}
            {this.props.affiliate.name.toLowerCase()} #{this.props.affiliate.id}{" "}
            found
          </div>
        );
      } else {
        const hasColumns = admins?.some(
          admin => admin.email !== null && admin.phone !== null
        );

        return (
          <div>
            <br />
            Admins from {this.props.affiliate.name.toLowerCase()} #
            {this.props.affiliate.id} who may be able to assist:
            {hasColumns && <AdminTable admins={admins} />}
            {!hasColumns && <AdminList admins={admins} />}
          </div>
        );
      }
    } else {
      return null;
    }
  }
}
const mapStateToProps = state => ({
  adminList: state.profileInfo.adminList
});

const mapDispatchToProps = { fetchAdminContacts };

class AdminContactErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error displaying AdminContactsCards component");
    console.error(error);
    console.error(errorInfo);
  }

  render() {
    // If there's an error with AdminContactCards, we render this instead
    if (this.state.hasError) {
      return <div>Error trying to retrieve admins to contact</div>;
    }

    return <AdminContactCards {...this.props} />;
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AdminContactErrorBoundary);
